import React, { useEffect, useMemo, useState } from "react";
import {
  FaUsers,
  FaUserCheck,
  FaUserTimes,
} from "react-icons/fa";
import { axiosMaster, axiosSalary } from "../Components/api/axiosInstance";
import { useAuth } from "../Components/auth/AuthContext";

const DashboardHome = () => {

  /* ================= AUTH USER ================= */
  const { user } = useAuth();

  const groupId = user?.groupId;
  const userTypeId = user?.userTypeId;
  const restaurantId = user?.restaurantId;

  /* ================= STATE ================= */
  const [employeeData, setEmployeeData] = useState([]);
  const [presentData, setPresentData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ================= FETCH ================= */
  const fetchDashboard = async () => {
    try {
      if (!groupId) return;

      setLoading(true);

      /* ================= EMPLOYEE API (FIXED) ================= */
      const empRes = await axiosMaster.get(
        `/EmployeeMaster/GetByGroup?groupId=${groupId}`
      );

      let empList = empRes?.data || [];

      if (userTypeId === 4 && restaurantId) {
        empList = empList.filter(
          (emp) => emp.restId === restaurantId
        );
      }

      setEmployeeData(empList);

      /* ================= ATTENDANCE API ================= */
      const res = await axiosSalary.get(
        `/AttendanceLog/GetPresentTodayByGroup?groupId=${groupId}`
      );

      let presentList = res.data || [];

      if (userTypeId === 4 && restaurantId) {
        presentList = presentList.filter(
          (item) => item.restId === restaurantId
        );
      }

      setPresentData(presentList);

    } catch (err) {
      console.error("Dashboard API error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= AUTO REFRESH ================= */
  useEffect(() => {
    if (!groupId) return;

    fetchDashboard();

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchDashboard();
      }
    }, 30000);

    return () => clearInterval(interval);

  }, [groupId, userTypeId, restaurantId]);

  /* ================= CALCULATIONS (FIXED) ================= */

  // 1️⃣ Employee ID Set
  const employeeIdSet = useMemo(() => {
    return new Set(employeeData.map((emp) => emp.empId));
  }, [employeeData]);

  // 2️⃣ Valid + Unique Present IDs
  const presentIds = useMemo(() => {
    const set = new Set();

    presentData.forEach((p) => {
      if (employeeIdSet.has(p.empId)) {
        set.add(p.empId); // removes duplicates
      }
    });

    return set;
  }, [presentData, employeeIdSet]);

  // 3️⃣ Final Counts
  const totalEmployees = employeeIdSet.size;
  const presentCount = presentIds.size;
  const absentCount = Math.max(totalEmployees - presentCount, 0);

  /* ================= STATS ================= */
  const stats = [
    {
      title: "Total Employees",
      value: totalEmployees,
      icon: FaUsers,
      color: "from-blue-500 to-indigo-500",
    },
    {
      title: "Present Today",
      value: presentCount,
      icon: FaUserCheck,
      color: "from-green-500 to-emerald-500",
    },
    {
      title: "Absent Today",
      value: absentCount,
      icon: FaUserTimes,
      color: "from-red-500 to-rose-500",
    },
  ];

  /* ================= UI ================= */
  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg p-6 border">
        <h2 className="text-2xl font-bold text-slate-800">
          Dashboard Overview
        </h2>

        <p className="text-slate-500 mt-1">
          Welcome to{" "}
          <span className="font-semibold">
            BillTrace Attendance System
          </span>
        </p>

        {loading && (
          <p className="text-xs text-gray-400 mt-2">
            Updating data...
          </p>
        )}
      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">

        {stats.map((item, index) => {
          const Icon = item.icon;

          return (
            <div
              key={index}
              className="bg-white/80 backdrop-blur-md rounded-2xl shadow-md p-5 border hover:shadow-xl transition"
            >
              <div className="flex items-center justify-between">

                <div>
                  <p className="text-sm text-slate-500">
                    {item.title}
                  </p>

                  <h3 className="text-2xl font-bold text-slate-800 mt-1">
                    {item.value}
                  </h3>
                </div>

                <div
                  className={`p-3 rounded-xl text-white bg-gradient-to-r ${item.color}`}
                >
                  <Icon size={20} />
                </div>

              </div>
            </div>
          );
        })}

      </div>

    </div>
  );
};

export default DashboardHome;